import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	AlertTriangle,
	CheckCircle2,
	KeyRound,
	Loader2,
	Trash2,
	XCircle,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import {
	type AiCredential,
	getAiCredentialsQueryOptions,
	useDeleteAiCredential,
	useSetAiCredential,
	useTestAiCredential,
} from "@/api/aiCredentials";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { SettingsLayout } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/SettingsLayout";
import { requiredString } from "@/utils/formSchemas";

/**
 * SECURITY (G1): this card is secret-bearing. It MUST NOT opt into localStorage
 * form drafts — the API key must never be persisted to disk, and the key value
 * is never logged. A lint-style test (aiKeyFormNoDraft) pins this surface.
 */
const aiKeySchema = z.object({ apiKey: requiredString() });
type AiKeyFormValues = z.infer<typeof aiKeySchema>;

// OpenAI-only in-process this phase (G6). The schema/API stay provider-generic,
// so adding a provider here is the only UI change once its backend client lands.
const PROVIDER = "openai";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/ai-keys/",
)({
	loader: async ({ context, params }) => {
		context.queryClient.ensureQueryData(
			getAiCredentialsQueryOptions(params.orgId),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();

	const { data } = useSuspenseQuery(getAiCredentialsQueryOptions(orgId));
	const existing = data.credentials.find(
		(c: AiCredential) => c.provider === PROVIDER && c.isActive,
	);

	return (
		<SettingsLayout
			activeTab="ai-keys"
			title={t("settings.aiKeys.title")}
			testId="ai-keys-settings-container"
		>
			<div className="space-y-3 md:space-y-4">
				<p className="text-xs md:text-sm text-base-content/70">
					{t("settings.aiKeys.description")}
				</p>
				<OpenAiKeyCard orgId={orgId} existing={existing} />
			</div>
		</SettingsLayout>
	);
}

/** Exported for the demotion-rendering test; the route composes it above. */
export function OpenAiKeyCard({
	orgId,
	existing,
}: {
	orgId: string;
	existing?: AiCredential;
}) {
	const { t } = useTranslation();
	const { formatDateTime } = useDateFormatter();

	const setKey = useSetAiCredential();
	const testKey = useTestAiCredential();
	const deleteKey = useDeleteAiCredential();

	// The runtime demotion the backend records when a provider 401s on this key:
	// it is still stored and still `isActive`, but payer selection skips it and
	// billing has fallen back to the platform key. Rendering only `isActive`
	// showed a green "key on file" for a key that had stopped being used.
	const isRejected = Boolean(existing?.unhealthyAt);

	const form = useForm<AiKeyFormValues>({
		resolver: zodResolver(aiKeySchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: { apiKey: "" },
	});

	const guardedSubmit = useGuardedSubmit(form, (values) =>
		setKey
			.mutateAsync({
				organizationId: orgId,
				data: { provider: PROVIDER, apiKey: values.apiKey.trim() },
			})
			.then(() => form.reset({ apiKey: "" })),
	);

	const handleTest = async () => {
		const result = await testKey.mutateAsync({
			organizationId: orgId,
			provider: PROVIDER,
		});
		if (result.valid) {
			toast.success(t("settings.aiKeys.testValid"));
		} else {
			toast.error(t("settings.aiKeys.testInvalid"));
		}
	};

	const handleDelete = async () => {
		await deleteKey.mutateAsync({ organizationId: orgId, provider: PROVIDER });
	};

	return (
		<Card>
			<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
				<CardTitle className="flex items-center gap-2 text-sm md:text-base">
					<KeyRound className="w-4 h-4 md:w-5 md:h-5" />
					{t("settings.aiKeys.providers.openai")}
				</CardTitle>
			</CardHeader>
			<CardContent className="px-3 md:px-6 pb-3 md:pb-6 space-y-3 md:space-y-4">
				{existing ? (
					<div
						className={
							isRejected
								? "flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning/50 bg-warning/10 px-3 py-2"
								: "flex flex-wrap items-center justify-between gap-2 rounded-md border border-base-300 bg-base-200/60 px-3 py-2"
						}
					>
						<div className="flex items-center gap-2 text-sm">
							{isRejected ? (
								<AlertTriangle className="h-4 w-4 text-warning shrink-0" />
							) : (
								<CheckCircle2 className="h-4 w-4 text-success shrink-0" />
							)}
							<span className="text-base-content/70">
								{isRejected
									? t("settings.aiKeys.keyRejected")
									: t("settings.aiKeys.keyOnFile")}
							</span>
							<span className="font-mono">••••{existing.keyLast4}</span>
						</div>
						<div className="flex items-center gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={handleTest}
								disabled={testKey.isPending}
							>
								{testKey.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									t("settings.aiKeys.test")
								)}
							</Button>
							<Button
								type="button"
								variant="destructive"
								size="sm"
								onClick={handleDelete}
								disabled={deleteKey.isPending}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</div>
						{isRejected && (
							// Why it stopped working, when, and what to do — the three facts
							// the backend already records (`unhealthyReason`, `unhealthyAt`,
							// `remediation`) and the card used to discard. `unhealthyReason`
							// is deliberately a status code, never provider body text.
							<div
								className="w-full space-y-1 text-xs text-base-content/80"
								data-testid="ai-key-rejected-detail"
							>
								<p>{t("settings.aiKeys.keyRejectedBody")}</p>
								{existing.unhealthyAt && (
									<p className="text-base-content/60">
										{t("settings.aiKeys.keyRejectedSince", {
											date: formatDateTime(
												new Date(existing.unhealthyAt).getTime(),
											),
										})}
										{existing.unhealthyReason
											? ` — ${existing.unhealthyReason}`
											: ""}
									</p>
								)}
								{existing.remediation === "RESAVE_KEY" && (
									<p className="font-medium">
										{t("settings.aiKeys.remediation.RESAVE_KEY")}
									</p>
								)}
							</div>
						)}
					</div>
				) : (
					<div className="flex items-center gap-2 text-xs md:text-sm text-base-content/60">
						<XCircle className="h-4 w-4 shrink-0" />
						{t("settings.aiKeys.noKey")}
					</div>
				)}

				<Form {...form}>
					<form onSubmit={guardedSubmit} className="space-y-2">
						<FormField
							control={form.control}
							name="apiKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-xs md:text-sm">
										{existing
											? t("settings.aiKeys.replaceLabel")
											: t("settings.aiKeys.addLabel")}
									</FormLabel>
									<div className="flex flex-col gap-2 sm:flex-row">
										<FormControl>
											<Input
												{...field}
												type="password"
												autoComplete="off"
												placeholder="sk-..."
												className="font-mono"
											/>
										</FormControl>
										<SubmitButton pending={setKey.isPending}>
											{t("common.save")}
										</SubmitButton>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormRootMessage />
						<p className="text-xs text-base-content/60">
							{t("settings.aiKeys.securityNote")}
						</p>
					</form>
				</Form>
			</CardContent>
		</Card>
	);
}
